import useTranslator from "../../hooks/useTranslator";
import { IoIosArrowDown, IoIosArrowUp } from "react-icons/io";
import { Tooltip as ReactTooltip } from "react-tooltip";
import moment from "moment";
import { haveValue } from "../../helper/common";
import { DOCUMENT_CATEGORIES } from "../../Utils/document-categories";
import constants from "../../Utils/constants";
import {
  getRequiredPayslipPeriods,
  getPayslipDateWarning,
  isPayslipWithinRequiredPeriod,
} from "../../helper/common";
import {
  documentMatchesIdentifiers,
  getTenantDocumentIdentifiers,
} from "../../Utils/tenantDocumentNavigation";

const getUniqueDocKey = (doc) =>
  doc?.documentAnalysisData?.instanceId ??
  doc?.documentAnalysisData?.data?.instanceId ??
  doc?.instanceId ??
  doc?.bucketKey ??
  doc?.bucket_key ??
  doc?.documentAnalysisData?.bucket_key ??
  doc?.documentAnalysisData?.data?.bucket_key ??
  doc?.documentAnalysisData?.bucketName ??
  doc?.bucketName ??
  doc?.id ??
  doc?._id ??
  doc?.document_id ??
  doc?.file_name ??
  `${doc?.type ?? ""}-${doc?.categoryKey ?? ""}-${doc?.created_at ?? ""}`;

const dedupeDocs = (docs = []) => {
  const seen = new Set();
  return docs.filter((doc) => {
    if (!doc) return false;
    const key = getUniqueDocKey(doc);
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

const dedupeDocRows = (rows = []) => {
  const seen = new Set();
  return rows.filter((row) => {
    const doc = row?.doc;
    if (!doc) return true;
    const key = getUniqueDocKey(doc);
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

const getOwnerBadgeTranslationKey = (doc) =>
  doc?.analysisOwnerType === "legal_entity" ? "thirdPartyDoc" : "othersDocs";

// FilterTag props
// 1. isActive
// 2. onClick
// 3. count
// 4. name
export function FilterTag({ isActive = false, name, onClick, count = 0, src }) {
  return (
    <div className="col-6 col-md-3">
      <div
        className={`docFilterButton cursor-pointer ${isActive ? "active" : ""}`}
        onClick={onClick}
      >
        <div className="d-flex align-items-center docFilterButton-info">
          <img src={src} alt="" />
          <span className="label">{name}</span>
        </div>
        <span className="count">{count}</span>
      </div>
    </div>
  );
}

export function MissingDocsTag({ llShowOnly, tooltipIndex }) {
  const { _t } = useTranslator();

  if (llShowOnly) {
    return (
      <span
        className="missing-doc-msg d-flex align-items-center"
        data-tooltip-id={`missing-doc-icon-${tooltipIndex}`}
      >
        <img src="/images/missing-doc-icon-gray.svg" alt="" />
        {_t("missingDocs")}
      </span>
    );
  }

  return (
    <span className="missing-doc-msg missing-doc-msg-tenant">
      <img src="/images/warning.svg" alt="" />
      {_t("missingDocs")}
    </span>
  );
}

// Tooltip Components
export const ScannedTooltip = ({ index }) => {
  const { _t } = useTranslator();
  return (
    <ReactTooltip
      id={`scanned-doc-icon-${index}`}
      noArrow
      className="react-tooltip-styling details-skor-tooltip"
      render={() => (
        <p>{_t("thisSectionContainsScannedDocumentsThatCannotBeAnalyzed")}</p>
      )}
    />
  );
};

export const MissingDocTooltip = ({ index }) => {
  const { _t } = useTranslator();
  return (
    <ReactTooltip
      id={`missing-doc-icon-${index}`}
      noArrow
      className="react-tooltip-styling details-skor-tooltip"
      render={() => <p>{_t("missingDocTooltipDescription")}</p>}
    />
  );
};

export const WarningTooltip = ({ index }) => {
  const { _t } = useTranslator();
  return (
    <ReactTooltip
      id={`warning-doc-icon-${index}`}
      noArrow
      className="react-tooltip-styling details-skor-tooltip"
      render={() => <p>{_t("someDocumentsInThisSectionRequireReview")}</p>}
    />
  );
};

export const HighRiskTooltip = ({ index }) => {
  const { _t } = useTranslator();
  return (
    <ReactTooltip
      id={`high-risk-doc-icon-${index}`}
      noArrow
      className="react-tooltip-styling details-skor-tooltip"
      render={() => (
        <p>{_t("someDocumentsInThisSectionShowSignsOfTampering")}</p>
      )}
    />
  );
};

// NoDocumentsFound - Renders the "No document found" message when no sections have content
export function NoDocumentsFound({
  forensic_docs_map,
  employmentType,
  getDocsForType,
  getPayslipDocs,
  getRequiredProtectedHousingDocs,
  getOtherDocs,
  selectedVerdicts,
  matchesSelectedVerdicts,
}) {
  const { _t } = useTranslator();

  // Check if identification section would render
  const hasIdentification =
    (forensic_docs_map?.[employmentType]?.identification?.length ?? 0) > 0 &&
    (() => {
      const allDocs = forensic_docs_map[employmentType].identification;
      const availableDocs = dedupeDocs(
        allDocs.flatMap((item) =>
          getDocsForType(item, [DOCUMENT_CATEGORIES.IDENTIFICATION])
        )
      )
        .filter(Boolean)
        .filter((doc) => matchesSelectedVerdicts(doc));
      return !(selectedVerdicts.length > 0 && availableDocs.length === 0);
    })();

  const getCombinedDocs = (section) => {
    const compulsory = section?.compulsory ?? [];
    const additional = section?.additional ?? [];
    return [...compulsory, ...additional];
  };

  // Check if employment status section would render
  const hasEmploymentStatus =
    getCombinedDocs(forensic_docs_map?.[employmentType]?.employment_status)
      .length > 0 &&
    (() => {
      const allDocs = getCombinedDocs(
        forensic_docs_map[employmentType]?.employment_status
      );
      const filteredDocs = dedupeDocs(
        allDocs.flatMap((item) =>
          getDocsForType(item, [DOCUMENT_CATEGORIES.EMPLOYMENT_STATUS])
        )
      ).filter((doc) => matchesSelectedVerdicts(doc));
      return !(selectedVerdicts.length > 0 && filteredDocs.length === 0);
    })();

  // Check if income taxes section would render
  const hasIncomeTaxes =
    getCombinedDocs(forensic_docs_map?.[employmentType]?.income_taxes).length >
      0 &&
    (() => {
      const allDocs = getCombinedDocs(
        forensic_docs_map[employmentType]?.income_taxes
      );
      const filteredAllDocs = dedupeDocs(
        allDocs.flatMap((item) =>
          item?.toLowerCase()?.includes("payslip")
            ? getPayslipDocs()
            : getDocsForType(item, [
                DOCUMENT_CATEGORIES.TAX,
                DOCUMENT_CATEGORIES.INCOME,
                DOCUMENT_CATEGORIES.PAYMENT_RECEIPT,
                DOCUMENT_CATEGORIES.FINANCIAL,
              ])
        )
      ).filter((doc) => matchesSelectedVerdicts(doc));

      return !(selectedVerdicts.length > 0 && filteredAllDocs.length === 0);
    })();

  const hasProtectedHousing = (() => {
    const allDocs = getRequiredProtectedHousingDocs();

    if (allDocs.length === 0) return false;

    const filteredDocs = allDocs
      .flatMap((item) => getDocsForType(item))
      .filter((doc) => matchesSelectedVerdicts(doc));

    return !(selectedVerdicts.length > 0 && filteredDocs.length === 0);
  })();

  // Check if other documents section would render
  const hasOtherDocs = getOtherDocs?.length > 0;

  const noSectionsRendered =
    !hasIdentification &&
    !hasEmploymentStatus &&
    !hasIncomeTaxes &&
    !hasProtectedHousing &&
    !hasOtherDocs &&
    forensic_docs_map !== undefined;

  return noSectionsRendered ? (
    <div className="noDocFound">
      <div>
        <img src={"/images/no-document-image.svg"} alt="" />
      </div>
      <div>{_t("noDocumentsFound")}</div>
    </div>
  ) : null;
}

// DocumentRow - Reusable component for rendering a single document row
// Service-file tags shown on profile documents (TenantServiceFile.service).
// A document can carry multiple tags when several service files point to it.
export const SERVICE_TAG_I18N_KEYS = {
  KYC: "usedForKyc",
  EXPERIAN: "usedForDefaults",
  BANK_SCORE: "usedForBankScore",
};

export function DocServiceTags({ tags = [] }) {
  const { _t } = useTranslator();
  const keys = (Array.isArray(tags) ? tags : [])
    .map(
      (service) =>
        SERVICE_TAG_I18N_KEYS[
          String(service ?? "")
            .toUpperCase()
            .trim()
        ]
    )
    .filter(Boolean);
  if (keys.length === 0) return null;
  return (
    <span className="doc-service-tags">
      {keys.map((key) => (
        <span key={key} className="doc-service-tag">
          {/* <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1 1 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z" />
            <path d="m9 12 2 2 4-4" />
          </svg> */}
          {_t(key)}
        </span>
      ))}
    </span>
  );
}

export function DocumentRow({
  doc,
  index,
  llShowOnly,
  readOnly = false,
  forensicDocumentReport,
  currentLang,
  isOtherUserDoc,
  getDocOwnerLabel,
  getDisplayDocName,
  forensicAnalysis,
  setDeleteDocumentConfirmation,
  setselectedDocument,
  handleViewDocument,
  verdictPriority,
  _t,
  tooltipIndex = 0,
  getDocServiceTags = () => [],
  highlightedDocumentIdentifiers = [],
}) {
  const getBorderColor = () => {
    if (llShowOnly) {
      if (doc?.type === "Declaration Consent") return "transparent";
      const report = forensicDocumentReport?.[doc?.file_name];
      if (report?.verdict === "HIGH_RISK") return "#ffe2e2";
      if (
        report?.documentType === "is_scanned_document" ||
        report?.isScannedDocument === "yes"
      )
        return "#fffbec";
      if (report?.verdict === "WARNING") return "#fffbec";
      if (report?.verdict === "NORMAL") return "transparent";
      if (report?.verdict === "TRUSTED") return "transparent";
      return !doc ? "rgb(197 193 193)" : "transparent";
    } else {
      return isOtherUserDoc(doc) ? "#FC9597" : !doc ? "#FACD7D" : "transparent";
    }
  };

  const getIconSrc = () => {
    if (!doc) return "/images/missing-icon.svg";

    if (doc?.type === "Declaration Consent") return "/images/normal-icon.svg";

    const report = forensicDocumentReport?.[doc?.file_name];
    if (
      report?.documentType === "is_scanned_document" ||
      report?.isScannedDocument === "yes"
    )
      return "/images/search-cross-with-bg.svg";
    if (report?.verdict === "HIGH_RISK") return "/images/high-risk-icon.svg";
    if (report?.verdict === "WARNING") return "/images/warning-icon.svg";
    if (report?.verdict === "NORMAL") return "/images/normal-icon.svg";
    if (report?.verdict === "TRUSTED") return "/images/trusted-icon.svg";

    return "/images/warning-icon.svg";
  };

  const renderTooltip = () => {
    if (!llShowOnly) return null;

    const report = forensicDocumentReport?.[doc?.file_name];
    const hasReportData =
      haveValue(report?.explanationSummary?.["en"]) ||
      haveValue(report?.recommendedAction?.["en"]) ||
      haveValue(report?.score);

    if (!hasReportData && doc?.type !== "Declaration Consent") {
      return (
        <ReactTooltip
          id={`doc-tooltip-${tooltipIndex}-${doc?._id}`}
          noArrow
          className="react-tooltip-styling details-skor-tooltip"
          render={() => (
            <p>
              <span>{_t("explanationSummary")}: </span>
              {_t(
                "notAbleToRunForensicCheckForThisDocument",
                "Fall back text in document section tooltip when for a document forensic was not able to run.(Shown on hover)"
              )}
            </p>
          )}
        />
      );
    }

    const lang = currentLang === "en-us" ? "en" : "es";
    const isScanned =
      report?.documentType === "is_scanned_document" ||
      report?.isScannedDocument === "yes";

    return (
      <ReactTooltip
        id={`doc-tooltip-${tooltipIndex}-${doc?._id}`}
        noArrow
        className="react-tooltip-styling details-skor-tooltip"
        render={() => (
          <div>
            {report?.explanationSummary?.[lang] && (
              <>
                <p>
                  <span>{_t("explanationSummary")}: </span>
                  {isScanned
                    ? _t("thisDocumentIsAScanSoCouldNotRunForensicAnalysis")
                    : report?.explanationSummary?.[lang]}
                </p>
                <hr className="document-dropdown-tooltip-hr" />
              </>
            )}
            {report?.recommendedAction?.[lang] && (
              <p>
                <span>{_t("recommendedAction")}: </span>
                {isScanned
                  ? _t(
                      "toEnableForensicChecksAskTheTenantToUploadTheOriginalDigitalPdf"
                    )
                  : report?.recommendedAction?.[lang]}
              </p>
            )}
            {!isScanned && report?.score && (
              <p>
                <span>{_t("confidenceInAuthenticity")}: </span>
                {`${report.score}%`}
              </p>
            )}
          </div>
        )}
      />
    );
  };

  return (
    <div
      key={doc?._id || index}
      className={`d-flex doc-inner-container-padding justify-content-between position-relative align-items-center ${
        documentMatchesIdentifiers(doc, highlightedDocumentIdentifiers)
          ? "tenant-document-navigation-highlight"
          : ""
      }`}
      data-tenant-document-identifiers={JSON.stringify(
        getTenantDocumentIdentifiers(doc)
      )}
      style={{
        opacity: !doc ? 0.4 : 1,
        pointerEvents: !doc ? "none" : "",
        borderColor: getBorderColor(),
      }}
    >
      <div className="d-flex align-items-center doc-name-wrapper">
        {llShowOnly && (
          <>
            <img
              data-tooltip-id={`doc-tooltip-${tooltipIndex}-${doc?._id}`}
              src={getIconSrc()}
              alt=""
            />
            {renderTooltip()}
          </>
        )}
        <div>
          <p className="docName">
            {getDisplayDocName(doc)}
            <DocServiceTags tags={getDocServiceTags(doc)} />
            {doc?.type === "Working life certificate" &&
              haveValue(
                forensicAnalysis?.["Work Life Certificate"]?.dataInJSON
                  ?.reportDate
              ) && (
                <>
                  {" "}
                  (
                  {moment(
                    forensicAnalysis?.["Work Life Certificate"]?.dataInJSON
                      ?.reportDate
                  ).format("D MMMM YYYY")}
                  )
                </>
              )}
          </p>
          {doc && (
            <p className="doc-uploaded-date d-flex align-items-center">
              <span className="doc-uploaded-date-text">
                {_t("uploadedOn")}{" "}
                {moment(doc?.created_at).format("DD-MM-YYYY")}
              </span>
              {isOtherUserDoc(doc) && (
                <span className="others-doc-badge">
                  <img src="/images/user-icon.svg" alt="" />
                  <span title={`${getDocOwnerLabel(doc)}`}>
                    {_t(getOwnerBadgeTranslationKey(doc))}:{" "}
                    {getDocOwnerLabel(doc)}
                  </span>
                </span>
              )}
            </p>
          )}
        </div>
      </div>
      <div className="d-flex align-items-center docs-view-btn-gap">
        {isOtherUserDoc(doc) && !llShowOnly && !readOnly && (
          <div
            onClick={() => {
              setDeleteDocumentConfirmation(true);
              setselectedDocument(doc);
            }}
            className="delete cursor-pointer"
          >
            <img
              src="/images/delete-icon-green.svg"
              alt=""
              className="deleteIcon"
            />
          </div>
        )}
        {doc && (
          <div
            onClick={(e) => handleViewDocument(e, doc)}
            className="cursor-pointer d-flex align-items-center view-doc-button"
          >
            <img
              src="/images/view-eye-icon.svg"
              alt=""
              className="deleteIcon"
            />
          </div>
        )}
      </div>
    </div>
  );
}

// OtherDocsSection - Renders the "Other documents" section
export function OtherDocsSection({
  otherDocs,
  llShowOnly,
  readOnly = false,
  handleDropdownToggle,
  showDropdown,
  forensicDocumentReport,
  documentsByType,
  currentLang,
  isOtherUserDoc,
  getDocOwnerLabel,
  getDisplayDocName,
  setDeleteDocumentConfirmation,
  setselectedDocument,
  handleViewDocument,
  verdictPriority,
  forensicAnalysis,
  getDocServiceTags = () => [],
  highlightedDocumentIdentifiers = [],
  showClientIdentificationMissingDoc = false,
}) {
  const { _t } = useTranslator();
  showClientIdentificationMissingDoc =
    showClientIdentificationMissingDoc &&
    !(otherDocs ?? []).some(
      (doc) => doc?.type === "Client Identification Form"
    );
  if (!otherDocs?.length && !showClientIdentificationMissingDoc) return null;

  let hasHighRisk = false;
  let hasWarning = false;
  let hasOthersDocument = false;
  let hasThirdPartyDocument = false;
  let isScannedDoc = false;

  for (const item of otherDocs) {
    const fileName = documentsByType?.[item?.type]?.file_name;
    let verdict =
      item?.type === "Declaration Consent"
        ? "NORMAL"
        : forensicDocumentReport?.[fileName]?.verdict;
    const isScanned =
      item?.type === "Declaration Consent"
        ? false
        : forensicDocumentReport?.[fileName]?.documentType ===
            "is_scanned_document" ||
          forensicDocumentReport?.[fileName]?.isScannedDocument === "yes";
    if (isScanned) {
      isScannedDoc = true;
      continue;
    }
    if (verdict === "HIGH_RISK") hasHighRisk = true;
    if (verdict === "WARNING") hasWarning = true;
  }

  for (const doc of otherDocs) {
    if (isOtherUserDoc(doc)) {
      hasOthersDocument = true;
      if (doc?.analysisOwnerType === "legal_entity") {
        hasThirdPartyDocument = true;
        break;
      }
    }
  }

  const getPriority = (doc) => {
    if (!doc) return -1;
    if (doc?.type === "Declaration Consent")
      return verdictPriority["NORMAL"] ?? 3;
    const isScanned =
      forensicDocumentReport?.[doc?.file_name]?.documentType ===
        "is_scanned_document" ||
      forensicDocumentReport?.[doc?.file_name]?.isScannedDocument === "yes";
    if (isScanned) return verdictPriority["SCANNED"];
    const verdict = forensicDocumentReport?.[doc?.file_name]?.verdict;
    if (!verdict) return verdictPriority["WARNING"];
    return verdictPriority[verdict] ?? 3;
  };

  const sortedDocs = [...otherDocs].sort(
    (a, b) => getPriority(a) - getPriority(b)
  );

  return (
    <div className="documents-container-info-card">
      <div
        onClick={() => handleDropdownToggle("fourth")}
        className="d-flex justify-content-between cursor-pointer documents-container-info-card-padding position-relative align-items-center"
      >
        <div className="documents-container-info-card-img">
          <img src="/images/file-icon.svg" alt="" />
          <span>{_t("otherDocuments")}</span>
        </div>
        <div className="d-flex align-items-center custom-gap-2">
          {!llShowOnly && hasOthersDocument && (
            <span className="other-doc-msg d-flex align-items-center custom-gap-2">
              <img src="/images/high-risk.svg" alt="" />
              {_t(hasThirdPartyDocument ? "thirdPartyDoc" : "othersDocs")}
            </span>
          )}
          {llShowOnly && isScannedDoc && (
            <div
              className="documents-container-info-card-icon"
              data-tooltip-id="scanned-doc-icon-4"
            >
              <img
                className="scanned-cross-icon"
                src="/images/search-with-cross-icon.svg"
                alt=""
              />
            </div>
          )}
          {(hasHighRisk || hasWarning) && llShowOnly && (
            <div className="documents-container-info-card-icon">
              {hasWarning && (
                <img
                  src="/images/warning.svg"
                  alt=""
                  data-tooltip-id="warning-doc-icon-4"
                />
              )}
              {hasHighRisk && (
                <img
                  src="/images/high-risk.svg"
                  alt=""
                  data-tooltip-id="high-risk-doc-icon-4"
                />
              )}
            </div>
          )}
          {showClientIdentificationMissingDoc && (
            <MissingDocsTag llShowOnly={llShowOnly} tooltipIndex={4} />
          )}
          <ScannedTooltip index={4} />
          <WarningTooltip index={4} />
          <HighRiskTooltip index={4} />
          <div className="doc-pl">
            <span>{_t("documents")}: </span>
            <span>
              {showClientIdentificationMissingDoc
                ? `${otherDocs?.length ?? 0}/${(otherDocs?.length ?? 0) + 1}`
                : otherDocs?.length}
            </span>
          </div>
          <div className="cursor-pointer arrow-icon-docs">
            {showDropdown.fourth ? (
              <IoIosArrowUp size={18} style={{ color: "#253837" }} />
            ) : (
              <IoIosArrowDown size={18} style={{ color: "#253837" }} />
            )}
          </div>
        </div>
      </div>
      {showDropdown.fourth && (
        <div className="doc-inner-container">
          {showClientIdentificationMissingDoc && (
            <div
              className="d-flex doc-inner-container-padding justify-content-between position-relative align-items-center"
              style={{
                borderColor: llShowOnly ? "rgb(197 193 193)" : "#FACD7D",
              }}
            >
              <div className="d-flex align-items-center doc-name-wrapper">
                {llShowOnly && (
                  <img
                    src="/images/missing-icon.svg"
                    alt=""
                    style={{ opacity: 0.4 }}
                  />
                )}
                <div>
                  <p className="docName" style={{ opacity: 0.4 }}>
                    {_t("viviaClientIdentificationForm")}
                  </p>
                  <p className="doc-uploaded-date d-flex align-items-center">
                    <span className="doc-uploaded-date-text">
                      {_t("viviaFormNotSignedYet")}
                    </span>
                  </p>
                </div>
              </div>
              <div
                className="ms-5 d-flex align-items-center view-doc-button"
                style={{ opacity: 0.4 }}
              >
                {_t("documentMissing")}
              </div>
            </div>
          )}
          {sortedDocs.map((doc, index) => (
            <DocumentRow
              key={doc?._id || index}
              doc={doc}
              index={index}
              llShowOnly={llShowOnly}
              readOnly={readOnly}
              forensicDocumentReport={forensicDocumentReport}
              currentLang={currentLang}
              isOtherUserDoc={isOtherUserDoc}
              getDocOwnerLabel={getDocOwnerLabel}
              getDisplayDocName={getDisplayDocName}
              forensicAnalysis={forensicAnalysis}
              setDeleteDocumentConfirmation={setDeleteDocumentConfirmation}
              setselectedDocument={setselectedDocument}
              handleViewDocument={handleViewDocument}
              verdictPriority={verdictPriority}
              _t={_t}
              tooltipIndex={4}
              getDocServiceTags={getDocServiceTags}
              highlightedDocumentIdentifiers={highlightedDocumentIdentifiers}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ProtectedHousingSection - Renders the "Protected Housing" section
// This section can have missing documents (doc can be null)
export function ProtectedHousingSection({
  llShowOnly,
  readOnly = false,
  handleDropdownToggle,
  showDropdown,
  forensicDocumentReport,
  currentLang,
  isOtherUserDoc,
  getDocOwnerLabel,
  getDisplayDocName,
  setDeleteDocumentConfirmation,
  setselectedDocument,
  handleViewDocument,
  verdictPriority,
  // Function-based props
  getRequiredProtectedHousingDocs,
  getDocsForType,
  selectedVerdicts,
  matchesSelectedVerdicts,
  getDocServiceTags = () => [],
  highlightedDocumentIdentifiers = [],
}) {
  const { _t } = useTranslator();
  const allDocs = getRequiredProtectedHousingDocs();

  if (allDocs.length === 0) return null;

  const protectedRows = allDocs.flatMap((item) => {
    const docs = getDocsForType(item);
    return docs.length
      ? docs.map((doc) => ({ item, doc }))
      : [{ item, doc: null }];
  });

  const filteredDocs =
    selectedVerdicts.length === 0
      ? protectedRows
      : protectedRows.filter(({ doc }) => doc && matchesSelectedVerdicts(doc));

  if (selectedVerdicts.length > 0 && filteredDocs.length === 0) return null;

  const totalDocs = filteredDocs.length;
  const uploadedDocs = filteredDocs.filter(({ doc }) => !!doc).length;

  let hasHighRisk = false;
  let hasWarning = false;
  let hasOthersDocument = false;
  let isScannedDoc = false;

  for (const { doc } of filteredDocs) {
    const fileName = doc?.file_name;
    const verdict = forensicDocumentReport?.[fileName]?.verdict;

    const isScanned =
      forensicDocumentReport?.[fileName]?.documentType ===
        "is_scanned_document" ||
      forensicDocumentReport?.[fileName]?.isScannedDocument === "yes";
    if (isScanned) {
      isScannedDoc = true;
      continue;
    }
    if (verdict === "HIGH_RISK") hasHighRisk = true;
    if (verdict === "WARNING") hasWarning = true;
  }

  const hasMissingDocs = uploadedDocs < totalDocs;

  for (const { doc } of filteredDocs) {
    if (isOtherUserDoc(doc)) {
      hasOthersDocument = true;
      break;
    }
  }

  const getPriority = (doc) => {
    if (!doc) return -1;
    const isScanned =
      forensicDocumentReport?.[doc?.file_name]?.documentType ===
        "is_scanned_document" ||
      forensicDocumentReport?.[doc?.file_name]?.isScannedDocument === "yes";
    if (isScanned) return verdictPriority["SCANNED"];
    const verdict = forensicDocumentReport?.[doc?.file_name]?.verdict;
    if (!verdict) return verdictPriority["WARNING"];
    return verdictPriority[verdict] ?? 3;
  };

  const sortedDocs = [...filteredDocs].sort((a, b) => {
    const docA = a?.doc;
    const docB = b?.doc;
    return getPriority(docA) - getPriority(docB);
  });

  return (
    <div className="documents-container-info-card">
      <div
        onClick={() => handleDropdownToggle("protectedHousing")}
        className="d-flex cursor-pointer justify-content-between documents-container-info-card-padding position-relative"
      >
        <div className="documents-container-info-card-img">
          <img src="/images/file-icon.svg" alt="" />
          <span>{_t("protectedHousing")}</span>
        </div>
        <div className="d-flex align-items-center custom-gap-2">
          {!llShowOnly && hasOthersDocument && (
            <span className="other-doc-msg d-flex align-items-center custom-gap-2">
              <img src="/images/high-risk.svg" alt="" />
              {_t("othersDocs")}
            </span>
          )}
          {llShowOnly && isScannedDoc && (
            <div
              className="documents-container-info-card-icon"
              data-tooltip-id="scanned-doc-icon-5"
            >
              <img
                className="scanned-cross-icon"
                src="/images/search-with-cross-icon.svg"
                alt=""
              />
            </div>
          )}
          {(hasHighRisk || hasWarning || hasMissingDocs) && (
            <div className="documents-container-info-card-icon">
              {hasMissingDocs && (
                <MissingDocsTag llShowOnly={llShowOnly} tooltipIndex={5} />
              )}
              {hasWarning && llShowOnly && (
                <img
                  src="/images/warning.svg"
                  alt=""
                  data-tooltip-id="warning-doc-icon-5"
                />
              )}
              {hasHighRisk && llShowOnly && (
                <img
                  src="/images/high-risk.svg"
                  alt=""
                  data-tooltip-id="high-risk-doc-icon-5"
                />
              )}
            </div>
          )}
          <ScannedTooltip index={5} />
          <MissingDocTooltip index={5} />
          <WarningTooltip index={5} />
          <HighRiskTooltip index={5} />
          <div className="doc-pl">
            <span>{_t("documents")}: </span>
            <span>
              {uploadedDocs}/{totalDocs}
            </span>
          </div>
          <div className="cursor-pointer arrow-icon-docs">
            {showDropdown.protectedHousing ? (
              <IoIosArrowUp size={18} style={{ color: "#253837" }} />
            ) : (
              <IoIosArrowDown size={18} style={{ color: "#253837" }} />
            )}
          </div>
        </div>
      </div>

      {showDropdown.protectedHousing && (
        <div className="doc-inner-container">
          {sortedDocs.map(({ item, doc }) => {
            const title = getDisplayDocName(doc) || _t(item, "", "fixed");

            return (
              <div
                key={doc?.file_name ?? `${item}-missing`}
                className={`d-flex doc-inner-container-padding justify-content-between position-relative align-items-center ${
                  documentMatchesIdentifiers(
                    doc,
                    highlightedDocumentIdentifiers
                  )
                    ? "tenant-document-navigation-highlight"
                    : ""
                }`}
                data-tenant-document-identifiers={JSON.stringify(
                  getTenantDocumentIdentifiers(doc)
                )}
                style={{
                  opacity: !doc ? 0.4 : 1,
                  pointerEvents: !doc ? "none" : "",
                  borderColor: llShowOnly
                    ? forensicDocumentReport?.[doc?.file_name]?.verdict ===
                      "HIGH_RISK"
                      ? "#ffe2e2"
                      : forensicDocumentReport?.[doc?.file_name]
                            ?.documentType === "is_scanned_document" ||
                          forensicDocumentReport?.[doc?.file_name]
                            ?.isScannedDocument === "yes"
                        ? "#fffbec"
                        : forensicDocumentReport?.[doc?.file_name]?.verdict ===
                            "WARNING"
                          ? "#fffbec"
                          : forensicDocumentReport?.[doc?.file_name]
                                ?.verdict === "NORMAL"
                            ? "transparent"
                            : forensicDocumentReport?.[doc?.file_name]
                                  ?.verdict === "TRUSTED"
                              ? "transparent"
                              : !doc
                                ? "rgb(197 193 193)"
                                : "transparent"
                    : isOtherUserDoc(doc)
                      ? "#FC9597"
                      : !doc
                        ? "#FACD7D"
                        : "transparent",
                }}
              >
                <div className="d-flex align-items-center doc-name-wrapper">
                  {llShowOnly && (
                    <img
                      data-tooltip-id={`doc-fifth-dropdown-${doc?._id || item}`}
                      src={
                        forensicDocumentReport?.[doc?.file_name]
                          ?.documentType === "is_scanned_document" ||
                        forensicDocumentReport?.[doc?.file_name]
                          ?.isScannedDocument === "yes"
                          ? "/images/search-cross-with-bg.svg"
                          : forensicDocumentReport?.[doc?.file_name]
                                ?.verdict === "HIGH_RISK"
                            ? "/images/high-risk-icon.svg"
                            : forensicDocumentReport?.[doc?.file_name]
                                  ?.verdict === "WARNING"
                              ? "/images/warning-icon.svg"
                              : forensicDocumentReport?.[doc?.file_name]
                                    ?.verdict === "NORMAL"
                                ? "/images/normal-icon.svg"
                                : forensicDocumentReport?.[doc?.file_name]
                                      ?.verdict === "TRUSTED"
                                  ? "/images/trusted-icon.svg"
                                  : doc
                                    ? "/images/warning-icon.svg"
                                    : "/images/missing-icon.svg"
                      }
                      alt=""
                    />
                  )}
                  <div>
                    <p className="docName">
                      {title}
                      <DocServiceTags tags={getDocServiceTags(doc)} />
                    </p>
                    <p className="doc-uploaded-date d-flex align-items-center">
                      <span className="doc-uploaded-date-text">
                        {doc ? (
                          <>
                            {_t("uploadedOn")}{" "}
                            {moment(doc?.created_at).format("DD-MM-YYYY")}
                          </>
                        ) : (
                          _t("documentMissing")
                        )}
                      </span>
                      {isOtherUserDoc(doc) && (
                        <span className="others-doc-badge">
                          <img src="/images/user-icon.svg" alt="" />
                          <span title={`${getDocOwnerLabel(doc)}`}>
                            {_t(getOwnerBadgeTranslationKey(doc))}:{" "}
                            {getDocOwnerLabel(doc)}
                          </span>
                        </span>
                      )}
                    </p>
                  </div>
                </div>
                <div className="d-flex align-items-center docs-view-btn-gap">
                  {isOtherUserDoc(doc) && !llShowOnly && !readOnly && (
                    <div
                      onClick={() => {
                        setDeleteDocumentConfirmation(true);
                        setselectedDocument(doc);
                      }}
                      className="delete cursor-pointer"
                    >
                      <img
                        src="/images/delete-icon-green.svg"
                        alt=""
                        className="deleteIcon"
                      />
                    </div>
                  )}
                  {doc && (
                    <div
                      onClick={(e) => handleViewDocument(e, doc)}
                      className="underline cursor-pointer d-flex align-items-center view-doc-button"
                    >
                      <img
                        src="/images/view-eye-icon.svg"
                        alt=""
                        className="deleteIcon"
                      />
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// IdentificationSection - Renders the "Identification" section
export function IdentificationSection({
  llShowOnly,
  readOnly = false,
  handleDropdownToggle,
  showDropdown,
  forensicDocumentReport,
  currentLang,
  isOtherUserDoc,
  getDocOwnerLabel,
  getDisplayDocName,
  setDeleteDocumentConfirmation,
  setselectedDocument,
  handleViewDocument,
  verdictPriority,
  forensicAnalysis,
  // Function-based props
  forensicDocsMap,
  employmentType,
  getDocsForType,
  selectedVerdicts,
  matchesSelectedVerdicts,
  showOnfidoCustomTooltipMessages,
  getDocServiceTags = () => [],
  highlightedDocumentIdentifiers = [],
}) {
  const { _t } = useTranslator();
  const allDocs = forensicDocsMap?.[employmentType]?.identification ?? [];

  if (allDocs.length === 0) return null;

  let availableDocs = allDocs
    .flatMap((item) =>
      getDocsForType(item, [DOCUMENT_CATEGORIES.IDENTIFICATION])
    )
    .filter(Boolean);

  availableDocs = dedupeDocs(availableDocs)
    .filter(Boolean)
    .filter((doc) => matchesSelectedVerdicts(doc));

  // Removing section if there is no document in filter found
  if (selectedVerdicts.length > 0 && availableDocs.length === 0) return null;

  const getDocPriority = (doc) => {
    const isScanned =
      forensicDocumentReport?.[doc?.file_name]?.documentType ===
        "is_scanned_document" ||
      forensicDocumentReport?.[doc?.file_name]?.isScannedDocument === "yes";

    if (isScanned) return verdictPriority["SCANNED"];

    const verdict = forensicDocumentReport?.[doc?.file_name]?.verdict;

    if (!verdict) return verdictPriority["WARNING"];

    return verdictPriority[verdict] ?? 3;
  };

  availableDocs = [...availableDocs].sort(
    (a, b) => getDocPriority(a) - getDocPriority(b)
  );

  const totalDocs = availableDocs.length;
  const uploadedDocs = availableDocs.length;

  // 🔹 Detect verdicts present in this section
  let hasHighRisk = false;
  let hasWarning = false;
  let hasOthersDocument = false;
  let isScannedDoc = false;

  // detecting high risk and warning tag
  for (const doc of availableDocs) {
    const fileName = doc?.file_name;
    const verdict = forensicDocumentReport?.[fileName]?.verdict;
    const isScanned =
      forensicDocumentReport?.[fileName]?.documentType ===
        "is_scanned_document" ||
      forensicDocumentReport?.[fileName]?.isScannedDocument === "yes";
    if (isScanned) {
      isScannedDoc = true;
      continue;
    }
    if (verdict === "HIGH_RISK") hasHighRisk = true;
    if (verdict === "WARNING") hasWarning = true;
  }

  // detecting others document for tag
  for (const doc of availableDocs) {
    if (isOtherUserDoc(doc)) {
      hasOthersDocument = true;
      break;
    }
  }

  return (
    <div className="">
      <div className="documents-container-info-card">
        <div
          onClick={() => handleDropdownToggle("first")}
          className="d-flex cursor-pointer justify-content-between documents-container-info-card-padding position-relative"
        >
          <div className="documents-container-info-card-img">
            <img src="/images/identification.svg" alt="" />
            <span>{_t("identification")}</span>
          </div>
          <div className="d-flex align-items-center custom-gap-2">
            {!llShowOnly && hasOthersDocument && (
              <span className="other-doc-msg d-flex align-items-center custom-gap-2">
                <img src="/images/high-risk.svg" alt="" />
                {_t("othersDocs")}
              </span>
            )}
            {llShowOnly && isScannedDoc && (
              <div
                className="documents-container-info-card-icon"
                data-tooltip-id="scanned-doc-icon-1"
              >
                <img
                  className="scanned-cross-icon"
                  src="/images/search-with-cross-icon.svg"
                  alt=""
                />
              </div>
            )}
            {/* verdict text */}
            {(hasHighRisk || hasWarning) && llShowOnly && (
              <div className="documents-container-info-card-icon">
                {hasWarning && (
                  <img
                    src="/images/warning.svg"
                    alt=""
                    data-tooltip-id="warning-doc-icon-1"
                  />
                )}
                {hasHighRisk && (
                  <img
                    src="/images/high-risk.svg"
                    alt=""
                    data-tooltip-id="high-risk-doc-icon-1"
                  />
                )}
              </div>
            )}
            <ScannedTooltip index={1} />
            <WarningTooltip index={1} />
            <HighRiskTooltip index={1} />
            <div className="doc-pl">
              <span>{_t("documents")}: </span>
              <span>
                {uploadedDocs}/{totalDocs}
              </span>
            </div>
            <div className="cursor-pointer arrow-icon-docs">
              {showDropdown.first ? (
                <IoIosArrowUp size={18} style={{ color: "#253837" }} />
              ) : (
                <IoIosArrowDown size={18} style={{ color: "#253837" }} />
              )}
            </div>
          </div>
        </div>
        {showDropdown.first && (
          <div className="doc-inner-container">
            {availableDocs.map((doc) => (
              <div
                key={doc.file_name}
                className={`d-flex doc-inner-container-padding justify-content-between position-relative align-items-center ${
                  documentMatchesIdentifiers(
                    doc,
                    highlightedDocumentIdentifiers
                  )
                    ? "tenant-document-navigation-highlight"
                    : ""
                }`}
                data-tenant-document-identifiers={JSON.stringify(
                  getTenantDocumentIdentifiers(doc)
                )}
                style={{
                  opacity: !doc ? 0.4 : 1,
                  pointerEvents: !doc ? "none" : "",
                  borderColor: llShowOnly
                    ? forensicDocumentReport?.[doc?.file_name]?.verdict ===
                      "HIGH_RISK"
                      ? "#ffe2e2"
                      : forensicDocumentReport?.[doc?.file_name]
                            ?.documentType === "is_scanned_document" ||
                          forensicDocumentReport?.[doc?.file_name]
                            ?.isScannedDocument === "yes"
                        ? "#fffbec"
                        : forensicDocumentReport?.[doc?.file_name]?.verdict ===
                            "WARNING"
                          ? "#fffbec"
                          : forensicDocumentReport?.[doc?.file_name]
                                ?.verdict === "NORMAL"
                            ? "transparent"
                            : forensicDocumentReport?.[doc?.file_name]
                                  ?.verdict === "TRUSTED"
                              ? "transparent"
                              : !doc
                                ? "rgb(197 193 193)"
                                : "transparent"
                    : isOtherUserDoc(doc)
                      ? "#FC9597"
                      : !doc
                        ? "#FACD7D"
                        : "transparent",
                }}
              >
                <div className="d-flex align-items-center doc-name-wrapper">
                  {llShowOnly && (
                    <>
                      <img
                        data-tooltip-id={`doc-first-dropdown-${doc?._id}`}
                        src={
                          forensicDocumentReport?.[doc?.file_name]
                            ?.documentType === "is_scanned_document" ||
                          forensicDocumentReport?.[doc?.file_name]
                            ?.isScannedDocument === "yes"
                            ? "/images/search-cross-with-bg.svg"
                            : forensicDocumentReport?.[doc?.file_name]
                                  ?.verdict === "HIGH_RISK"
                              ? "/images/high-risk-icon.svg"
                              : forensicDocumentReport?.[doc?.file_name]
                                    ?.verdict === "WARNING"
                                ? "/images/warning-icon.svg"
                                : forensicDocumentReport?.[doc?.file_name]
                                      ?.verdict === "NORMAL"
                                  ? "/images/normal-icon.svg"
                                  : forensicDocumentReport?.[doc?.file_name]
                                        ?.verdict === "TRUSTED"
                                    ? "/images/trusted-icon.svg"
                                    : "/images/warning-icon.svg"
                        }
                        alt=""
                      />
                      {(() => {
                        const tooltipId = `doc-first-dropdown-${doc?._id}`;
                        const customMessage =
                          showOnfidoCustomTooltipMessages(doc);
                        const report = forensicDocumentReport?.[doc?.file_name];
                        const langKey = currentLang === "en-us" ? "en" : "es";
                        const isScanned =
                          report?.documentType === "is_scanned_document" ||
                          report?.isScannedDocument === "yes";

                        const hasCustomMessage = haveValue(customMessage);
                        const hasReportData =
                          haveValue(report?.explanationSummary?.["en"]) ||
                          haveValue(report?.recommendedAction?.["en"]) ||
                          haveValue(report?.score);

                        if (hasCustomMessage) {
                          return (
                            <ReactTooltip
                              id={tooltipId}
                              noArrow
                              className="react-tooltip-styling details-skor-tooltip"
                              render={() => (
                                <div>
                                  <p>
                                    <span>{_t("explanationSummary")}: </span>
                                    {customMessage?.explanation}
                                  </p>
                                  <hr className="document-dropdown-tooltip-hr" />
                                  <p>
                                    <span>{_t("recommendedAction")}: </span>
                                    {customMessage?.recommended_action}
                                  </p>
                                </div>
                              )}
                            />
                          );
                        }

                        if (hasReportData) {
                          const explanation = isScanned
                            ? _t(
                                "thisDocumentIsAScanSoCouldNotRunForensicAnalysis"
                              )
                            : report?.explanationSummary?.[langKey];

                          const action = isScanned
                            ? _t(
                                "toEnableForensicChecksAskTheTenantToUploadTheOriginalDigitalPdf"
                              )
                            : report?.recommendedAction?.[langKey];

                          return (
                            <ReactTooltip
                              id={tooltipId}
                              noArrow
                              className="react-tooltip-styling details-skor-tooltip"
                              render={() => (
                                <div>
                                  {explanation && (
                                    <>
                                      <p>
                                        <span>
                                          {_t("explanationSummary")}:{" "}
                                        </span>
                                        {explanation}
                                      </p>
                                      <hr className="document-dropdown-tooltip-hr" />
                                    </>
                                  )}
                                  {action && (
                                    <p>
                                      <span>{_t("recommendedAction")}: </span>
                                      {action}
                                    </p>
                                  )}
                                  {!isScanned && report?.score && (
                                    <p>
                                      <span>
                                        {_t("confidenceInAuthenticity")}:{" "}
                                      </span>
                                      {`${report.score}%`}
                                    </p>
                                  )}
                                </div>
                              )}
                            />
                          );
                        }

                        return (
                          <ReactTooltip
                            id={tooltipId}
                            noArrow
                            className="react-tooltip-styling details-skor-tooltip"
                            render={() => (
                              <p>
                                <span>{_t("explanationSummary")}: </span>
                                {_t(
                                  "notAbleToRunForensicCheckForThisDocument",
                                  "Fall back text in document section tooltip when for a document forensic was not able to run.(Shown on hover)"
                                )}
                              </p>
                            )}
                          />
                        );
                      })()}
                    </>
                  )}

                  <div className="">
                    <p className="docName">
                      {getDisplayDocName(doc)}
                      <DocServiceTags tags={getDocServiceTags(doc)} />
                      {doc?.type === "Working life certificate" &&
                        haveValue(
                          forensicAnalysis?.["Work Life Certificate"]
                            ?.dataInJSON?.reportDate
                        ) && (
                          <>
                            {" "}
                            (
                            {moment(
                              forensicAnalysis?.["Work Life Certificate"]
                                ?.dataInJSON?.reportDate
                            ).format("D MMMM YYYY")}
                            )
                          </>
                        )}
                    </p>
                    {doc && (
                      <p className="doc-uploaded-date d-flex align-items-center">
                        <span className="doc-uploaded-date-text">
                          {_t("uploadedOn")}{" "}
                          {moment(doc.created_at).format("DD-MM-YYYY")}
                        </span>
                        {isOtherUserDoc(doc) && (
                          <span className="others-doc-badge">
                            <img src="/images/user-icon.svg" alt="" />
                            <span title={`${getDocOwnerLabel(doc)}`}>
                              {_t(getOwnerBadgeTranslationKey(doc))}:{" "}
                              {getDocOwnerLabel(doc)}
                            </span>
                          </span>
                        )}
                      </p>
                    )}
                  </div>
                </div>
                <div className="d-flex align-items-center docs-view-btn-gap">
                  {isOtherUserDoc(doc) && !llShowOnly && !readOnly && (
                    <div
                      onClick={() => {
                        setDeleteDocumentConfirmation(true);
                        setselectedDocument(doc);
                      }}
                      className="delete cursor-pointer"
                    >
                      <img
                        src="/images/delete-icon-green.svg"
                        alt=""
                        className="deleteIcon"
                      />
                    </div>
                  )}
                  <div
                    onClick={(e) => handleViewDocument(e, doc)}
                    className="underline cursor-pointer d-flex align-items-center view-doc-button"
                  >
                    <img
                      src="/images/view-eye-icon.svg"
                      alt=""
                      className="deleteIcon"
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// IncomeTaxesSection - Renders the "Income and Taxes" section
// Handles special logic for payslips which can have multiple instances
export function IncomeTaxesSection({
  allForensicDocuments,
  llShowOnly,
  readOnly = false,
  handleDropdownToggle,
  showDropdown,
  forensicDocumentReport,
  currentLang,
  isOtherUserDoc,
  getDocOwnerLabel,
  getDisplayDocName,
  setDeleteDocumentConfirmation,
  setselectedDocument,
  handleViewDocument,
  verdictPriority,
  forensicAnalysis,
  // Function-based props
  forensicDocsMap,
  employmentType,
  getDocsForType,
  getPayslipDocs,
  selectedVerdicts,
  matchesSelectedVerdicts,
  user,
  getDocServiceTags = () => [],
  highlightedDocumentIdentifiers = [],
}) {
  const forensicRawData =
    user?.tenant_extra_field_id?.forensic_analysis?.profileReports ?? [];
  const allForensicDocumentData = (
    Array.isArray(forensicRawData) ? forensicRawData : []
  ).flatMap((entry) => {
    return (entry?.report?.documents_list || []).map((doc) => ({
      ...doc,
    }));
  });
  // #NOTE: allForensicDocuments has keys with the filenames and then inside the values there is data field,
  // inside that data field there is period_year, period_month.
  // also, these period_month and period_year are present inside the filename key's value.
  const { _t } = useTranslator();
  const compulsoryDocs =
    forensicDocsMap?.[employmentType]?.income_taxes?.compulsory ?? [];
  const additionalDocs =
    forensicDocsMap?.[employmentType]?.income_taxes?.additional ?? [];
  const allDocs = [...compulsoryDocs, ...additionalDocs];

  if (allDocs.length === 0) return null;

  const categoryMatchedExtraDocs = [];
  const incomeRows = allDocs.flatMap((item) => {
    if (item?.toLowerCase()?.includes("payslip")) {
      const payslipDocs = getPayslipDocs();
      // #NOTE: payslipDocs is an array of object , in which each object has key "documentAnalysisData",
      // inside which there is period_month and period_year, or else,
      // there is data feild and inside data we have period_month and period_year.

      return payslipDocs.length
        ? payslipDocs.map((doc) => ({ item, doc }))
        : compulsoryDocs.includes(item)
          ? [{ item, doc: null, _isPayslipMissing: true }]
          : [];
    }

    const docs = getDocsForType(item, [
      DOCUMENT_CATEGORIES.TAX,
      DOCUMENT_CATEGORIES.INCOME,
      DOCUMENT_CATEGORIES.PAYMENT_RECEIPT,
      DOCUMENT_CATEGORIES.FINANCIAL,
    ]);
    const strictTypeDocs = docs.filter((doc) => doc?.type === item);
    const fallbackCategoryDocs = docs.filter((doc) => doc?.type !== item);
    const model100FallbackDocs =
      strictTypeDocs.length === 0 && item?.toLowerCase()?.includes("model 100")
        ? fallbackCategoryDocs.filter(
            (doc) => doc?.categoryKey === DOCUMENT_CATEGORIES.TAX
          )
        : [];

    if (fallbackCategoryDocs.length > 0) {
      categoryMatchedExtraDocs.push(
        ...fallbackCategoryDocs.filter(
          (doc) => !model100FallbackDocs.includes(doc)
        )
      );
    }

    return strictTypeDocs.length
      ? strictTypeDocs.map((doc) => ({ item, doc }))
      : model100FallbackDocs.length
        ? model100FallbackDocs.map((doc) => ({ item, doc }))
        : compulsoryDocs.includes(item)
          ? [{ item, doc: null }]
          : [];
  });
  const finalIncomeRows = dedupeDocRows([
    ...incomeRows,
    ...dedupeDocs(categoryMatchedExtraDocs).map((doc) => ({
      item: doc?.type ?? "",
      doc,
    })),
  ]);

  const filteredDocs =
    selectedVerdicts.length === 0
      ? finalIncomeRows
      : finalIncomeRows.filter(
          ({ doc }) => doc && matchesSelectedVerdicts(doc)
        );

  if (selectedVerdicts.length > 0 && filteredDocs.length === 0) return null;

  // Detect verdicts present in this section
  let hasHighRisk = false;
  let hasWarning = false;
  let hasOthersDocument = false;
  let isScannedDoc = false;
  let hasPayslipDateWarning = false;

  for (const { doc } of filteredDocs) {
    const fileName = doc?.file_name;
    const verdict = forensicDocumentReport?.[fileName]?.verdict;
    const isScanned =
      forensicDocumentReport?.[fileName]?.documentType ===
        "is_scanned_document" ||
      forensicDocumentReport?.[fileName]?.isScannedDocument === "yes";
    if (isScanned) {
      isScannedDoc = true;
      continue;
    }
    if (verdict === "HIGH_RISK") hasHighRisk = true;
    if (verdict === "WARNING") hasWarning = true;
  }

  // Check payslip date warning for employed and public servant users
  const isThreePayslipRequired =
    user?.employment_status === constants.employed ||
    user?.employment_status === constants.public_servant;

  let requiredPayslipPeriods = [];
  let payslipDocWarnings = new Map();

  if (isThreePayslipRequired) {
    requiredPayslipPeriods = getRequiredPayslipPeriods();
    const payslipDocs = getPayslipDocs();

    for (const doc of payslipDocs) {
      const forensicData = allForensicDocuments?.[doc?.file_name];
      const payslipPeriodYear = forensicData?.data?.period_year;
      const payslipPeriodMonth = forensicData?.data?.period_month;

      if (!payslipPeriodYear || !payslipPeriodMonth) {
        continue;
      }

      const payslipObj = {
        period_year: payslipPeriodYear,
        period_month: payslipPeriodMonth,
        date: doc?.date,
        created_at: doc?.created_at,
      };

      const warningMessage = getPayslipDateWarning(
        payslipObj,
        requiredPayslipPeriods,
        _t
      );

      if (warningMessage) {
        payslipDocWarnings.set(doc?._id || doc?.file_name, warningMessage);
        hasPayslipDateWarning = true;
      }
    }
  }

  for (const { doc } of filteredDocs) {
    if (isOtherUserDoc(doc)) {
      hasOthersDocument = true;
      break;
    }
  }

  const getPriority = (entry) => {
    if (entry._isPayslipMissing) return -1;
    const doc = entry.doc;
    if (!doc) return -1;
    const isScanned =
      forensicDocumentReport?.[doc?.file_name]?.documentType ===
        "is_scanned_document" ||
      forensicDocumentReport?.[doc?.file_name]?.isScannedDocument === "yes";
    if (isScanned) return verdictPriority["SCANNED"];
    const verdict = forensicDocumentReport?.[doc?.file_name]?.verdict;
    if (!verdict) return verdictPriority["WARNING"];
    return verdictPriority[verdict] ?? 3;
  };

  const sortedDocs = [...filteredDocs].sort(
    (a, b) => getPriority(a) - getPriority(b)
  );

  // Rebuild payslip slots — always exactly 3 for required periods, plus any old docs
  const nonPayslipSorted = sortedDocs.filter(
    (entry) => !entry.item?.toLowerCase()?.includes("payslip")
  );

  let payslipSlots = [];
  if (isThreePayslipRequired && requiredPayslipPeriods.length > 0) {
    const allPayslipDocs =
      selectedVerdicts.length > 0
        ? getPayslipDocs().filter((doc) => matchesSelectedVerdicts(doc))
        : getPayslipDocs();
    const usedDocKeys = new Set();

    const requiredSlots = requiredPayslipPeriods.map((period) => {
      const matchingDoc = allPayslipDocs.find((doc) => {
        const key = getUniqueDocKey(doc);
        if (usedDocKeys.has(key)) return false;
        // const forensicData = allForensicDocuments?.[doc?.file_name];
        const forensicData = allForensicDocumentData?.find(
          (entry) => entry?.instanceId === doc?.documentAnalysisData?.instanceId
        );
        const payslipObj = {
          period_year: Number(
            forensicData?.data?.period_year ??
              doc?.documentAnalysisData?.data?.period_year ??
              doc?.documentAnalysisData?.period_year
          ),
          period_month: Number(
            forensicData?.data?.period_month ??
              doc?.documentAnalysisData?.data?.period_month ??
              doc?.documentAnalysisData?.period_month
          ),
          date: doc?.date,
          created_at: doc?.created_at,
        };
        const matched = isPayslipWithinRequiredPeriod(payslipObj, [period]);
        if (matched) usedDocKeys.add(key);
        return matched;
      });

      return matchingDoc
        ? { item: "payslip", doc: matchingDoc }
        : {
            item: "payslip",
            doc: null,
            _isPayslipMissing: true,
            _payslipPeriod: period,
          };
    });

    const oldPayslipDocs = allPayslipDocs.filter(
      (doc) => !usedDocKeys.has(getUniqueDocKey(doc))
    );

    payslipSlots = [
      ...requiredSlots,
      ...oldPayslipDocs.map((doc) => ({ item: "payslip", doc })),
    ];
  } else {
    const existingDocCount = sortedDocs.filter(
      (entry) => entry.item?.toLowerCase()?.includes("payslip") && entry.doc
    ).length;
    if (isThreePayslipRequired) {
      payslipSlots = [
        ...sortedDocs.filter(
          (entry) => entry.item?.toLowerCase()?.includes("payslip") && entry.doc
        ),
        ...Array.from({ length: Math.max(0, 3 - existingDocCount) }, () => ({
          item: "payslip",
          doc: null,
          _isPayslipMissing: true,
        })),
      ];
    } else {
      payslipSlots = sortedDocs.filter((entry) =>
        entry.item?.toLowerCase()?.includes("payslip")
      );
    }
  }

  payslipSlots?.sort((a, b) => getPriority(a) - getPriority(b));

  const hasMissingPayslipPeriods = payslipSlots.some(
    (entry) => entry._isPayslipMissing
  );

  const displayDocs = [...nonPayslipSorted, ...payslipSlots].sort(
    (a, b) => getPriority(a) - getPriority(b)
  );

  const totalDocs = displayDocs.length;
  const uploadedDocs = displayDocs.filter(({ doc }) => !!doc).length;
  const hasMissingDocs = uploadedDocs < totalDocs;

  return (
    <div className="documents-container-info-card">
      <div
        onClick={() => handleDropdownToggle("third")}
        className="d-flex justify-content-between cursor-pointer documents-container-info-card-padding position-relative align-items-center"
      >
        <div className="documents-container-info-card-img">
          <img src="/images/income-taxes-icon.svg" alt="" />
          <span>{_t("incomeAndTaxes")}</span>
        </div>
        <div className="d-flex align-items-center custom-gap-2">
          {!llShowOnly && hasOthersDocument && (
            <span className="other-doc-msg d-flex align-items-center custom-gap-2">
              <img src="/images/high-risk.svg" alt="" />
              {_t("othersDocs")}
            </span>
          )}
          {llShowOnly && isScannedDoc && (
            <div
              className="documents-container-info-card-icon"
              data-tooltip-id="scanned-doc-icon-3"
            >
              <img
                className="scanned-cross-icon"
                src="/images/search-with-cross-icon.svg"
                alt=""
              />
            </div>
          )}
          {(hasHighRisk ||
            hasWarning ||
            hasMissingDocs ||
            hasPayslipDateWarning ||
            hasMissingPayslipPeriods) && (
            <div className="documents-container-info-card-icon">
              {(hasMissingDocs || hasMissingPayslipPeriods) && (
                <MissingDocsTag llShowOnly={llShowOnly} tooltipIndex={2} />
              )}
              {hasWarning && llShowOnly && (
                <img
                  src="/images/warning.svg"
                  alt=""
                  data-tooltip-id="warning-doc-icon-3"
                />
              )}
              {hasHighRisk && llShowOnly && (
                <img
                  src="/images/high-risk.svg"
                  alt=""
                  data-tooltip-id="high-risk-doc-icon-3"
                />
              )}
            </div>
          )}
          <ScannedTooltip index={3} />
          <MissingDocTooltip index={2} />
          <WarningTooltip index={3} />
          <HighRiskTooltip index={3} />
          <div className="doc-pl">
            <span>{_t("documents")}: </span>
            <span>
              {uploadedDocs}/{totalDocs}
            </span>
          </div>
          <div className="cursor-pointer arrow-icon-docs">
            {showDropdown.third ? (
              <IoIosArrowUp size={18} style={{ color: "#253837" }} />
            ) : (
              <IoIosArrowDown size={18} style={{ color: "#253837" }} />
            )}
          </div>
        </div>
      </div>

      {showDropdown.third && (
        <div className="doc-inner-container">
          {displayDocs.map((entry, idx) => {
            // Missing payslip sentinel render
            if (entry._isPayslipMissing) {
              const periodLabel = entry._payslipPeriod
                ? `${entry._payslipPeriod.monthName} ${entry._payslipPeriod.period_year}`
                : "";
              return (
                <div
                  key={`payslip-missing-${entry._payslipPeriod?.period_year ?? ""}-${entry._payslipPeriod?.period_month ?? idx}`}
                  className="d-flex doc-inner-container-padding justify-content-between position-relative align-items-center"
                  style={{
                    opacity: 0.4,
                    borderColor: llShowOnly ? "rgb(197 193 193)" : "#FACD7D",
                  }}
                >
                  <div className="d-flex align-items-center doc-name-wrapper">
                    {llShowOnly && (
                      <img src="/images/missing-icon.svg" alt="" />
                    )}
                    <div className="">
                      <div className="docName">
                        <p>
                          {periodLabel
                            ? `${_t("payslip")} (${periodLabel})`
                            : _t("payslip")}
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="ms-5 d-flex align-items-center view-doc-button">
                    {_t("documentMissing")}
                  </div>
                </div>
              );
            }

            const doc = entry.doc;
            const item = entry.item;

            const tooltipId = `doc-third-dropdown-${doc?._id}`;
            const report = forensicDocumentReport?.[doc?.file_name];
            const lang = currentLang === "en-us" ? "en" : "es";
            const isScanned =
              report?.documentType === "is_scanned_document" ||
              report?.isScannedDocument === "yes";

            const hasReportData =
              haveValue(report?.explanationSummary?.["en"]) ||
              haveValue(report?.recommendedAction?.["en"]) ||
              haveValue(report?.score);

            const getBorderColor = () => {
              if (llShowOnly) {
                if (report?.verdict === "HIGH_RISK") return "#ffe2e2";
                if (isScanned) return "#fffbec";
                if (report?.verdict === "WARNING") return "#fffbec";
                if (report?.verdict === "NORMAL") return "transparent";
                if (report?.verdict === "TRUSTED") return "transparent";
                return !doc ? "rgb(197 193 193)" : "transparent";
              }
              return isOtherUserDoc(doc)
                ? "#FC9597"
                : !doc
                  ? "#FACD7D"
                  : "transparent";
            };

            const getIconSrc = () => {
              if (!doc) return "/images/missing-icon.svg";
              if (isScanned) return "/images/search-cross-with-bg.svg";
              if (report?.verdict === "HIGH_RISK")
                return "/images/high-risk-icon.svg";
              if (report?.verdict === "WARNING")
                return "/images/warning-icon.svg";
              if (report?.verdict === "NORMAL")
                return "/images/normal-icon.svg";
              if (report?.verdict === "TRUSTED")
                return "/images/trusted-icon.svg";
              return "/images/warning-icon.svg";
            };

            return (
              <div
                key={doc?._id || item || idx}
                className={`d-flex doc-inner-container-padding justify-content-between position-relative align-items-center ${
                  documentMatchesIdentifiers(
                    doc,
                    highlightedDocumentIdentifiers
                  )
                    ? "tenant-document-navigation-highlight"
                    : ""
                }`}
                data-tenant-document-identifiers={JSON.stringify(
                  getTenantDocumentIdentifiers(doc)
                )}
                style={{
                  opacity: !doc ? 0.4 : 1,
                  pointerEvents: !doc ? "none" : "",
                  borderColor: getBorderColor(),
                }}
              >
                <div className="d-flex align-items-center doc-name-wrapper">
                  {llShowOnly && (
                    <>
                      <img
                        data-tooltip-id={tooltipId}
                        src={getIconSrc()}
                        alt=""
                      />
                      {hasReportData ? (
                        <ReactTooltip
                          id={tooltipId}
                          noArrow
                          className="react-tooltip-styling details-skor-tooltip"
                          render={() => (
                            <div>
                              {report?.explanationSummary?.[lang] && (
                                <>
                                  <p>
                                    <span>{_t("explanationSummary")}: </span>
                                    {isScanned
                                      ? _t(
                                          "thisDocumentIsAScanSoCouldNotRunForensicAnalysis"
                                        )
                                      : report?.explanationSummary?.[lang]}
                                  </p>
                                  <hr className="document-dropdown-tooltip-hr" />
                                </>
                              )}
                              {report?.recommendedAction?.[lang] && (
                                <p>
                                  <span>{_t("recommendedAction")}: </span>
                                  {isScanned
                                    ? _t(
                                        "toEnableForensicChecksAskTheTenantToUploadTheOriginalDigitalPdf"
                                      )
                                    : report?.recommendedAction?.[lang]}
                                </p>
                              )}
                              {!isScanned && report?.score && (
                                <p>
                                  <span>
                                    {_t("confidenceInAuthenticity")}:{" "}
                                  </span>
                                  {`${report.score}%`}
                                </p>
                              )}
                            </div>
                          )}
                        />
                      ) : (
                        <ReactTooltip
                          id={tooltipId}
                          noArrow
                          className="react-tooltip-styling details-skor-tooltip"
                          render={() => (
                            <p>
                              <span>{_t("explanationSummary")}: </span>
                              {_t(
                                "notAbleToRunForensicCheckForThisDocument",
                                "Fall back text in document section tooltip when for a document forensic was not able to run.(Shown on hover)"
                              )}
                            </p>
                          )}
                        />
                      )}
                    </>
                  )}
                  <div className="">
                    <div className="d-flex gap-3 align-items-center docName">
                      <p className="">
                        {getDisplayDocName(doc)}
                        {doc?.type === "Working life certificate" &&
                          haveValue(
                            forensicAnalysis?.["Work Life Certificate"]
                              ?.dataInJSON?.reportDate
                          ) && (
                            <>
                              {" "}
                              (
                              {moment(
                                forensicAnalysis?.["Work Life Certificate"]
                                  ?.dataInJSON?.reportDate
                              ).format("D MMMM YYYY")}
                              )
                            </>
                          )}
                        {!doc && `${_t(item, "", "fixed")}`}
                      </p>
                      <DocServiceTags tags={getDocServiceTags(doc)} />
                      {/* {isThreePayslipRequired &&
                        doc?.type?.toLowerCase()?.includes("payslip") &&
                        payslipDocWarnings.has(doc?._id || doc?.file_name) && (
                          <p className="doc-warning-message d-flex align-items-center">
                            <img
                              src="/images/warning.svg"
                              alt=""
                              className="me-1 mt-1"
                              style={{ width: "14px", height: "14px" }}
                            />
                            <span>
                              {payslipDocWarnings.get(
                                doc?._id || doc?.file_name
                              )}
                            </span>
                          </p>
                        )
                        } */}
                    </div>
                    {doc && (
                      <p className="doc-uploaded-date d-flex align-items-center">
                        <span className="doc-uploaded-date-text">
                          {_t("uploadedOn")}{" "}
                          {moment(doc?.created_at).format("DD-MM-YYYY")}
                        </span>
                        {isOtherUserDoc(doc) && (
                          <span className="others-doc-badge">
                            <img src="/images/user-icon.svg" alt="" />
                            <span title={`${getDocOwnerLabel(doc)}`}>
                              {_t(getOwnerBadgeTranslationKey(doc))}:{" "}
                              {getDocOwnerLabel(doc)}
                            </span>
                          </span>
                        )}
                      </p>
                    )}
                  </div>
                </div>
                <div className="d-flex align-items-center docs-view-btn-gap">
                  {isOtherUserDoc(doc) && !llShowOnly && !readOnly && (
                    <div
                      onClick={() => {
                        setDeleteDocumentConfirmation(true);
                        setselectedDocument(doc);
                      }}
                      className="delete cursor-pointer"
                    >
                      <img
                        src="/images/delete-icon-green.svg"
                        alt=""
                        className="deleteIcon"
                      />
                    </div>
                  )}
                  <div
                    onClick={(e) => handleViewDocument(e, doc)}
                    className={`d-flex align-items-center view-doc-button ${
                      doc ? "underline cursor-pointer" : ""
                    }`}
                  >
                    {doc ? (
                      <img
                        src="/images/view-eye-icon.svg"
                        alt=""
                        className="deleteIcon"
                      />
                    ) : (
                      _t("documentMissing")
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// EmploymentStatusSection - Renders the "Employment Status" section
export function EmploymentStatusSection({
  llShowOnly,
  readOnly = false,
  handleDropdownToggle,
  showDropdown,
  forensicDocumentReport,
  currentLang,
  isOtherUserDoc,
  getDocOwnerLabel,
  getDisplayDocName,
  setDeleteDocumentConfirmation,
  setselectedDocument,
  handleViewDocument,
  verdictPriority,
  forensicAnalysis,
  // Function-based props
  forensicDocsMap,
  employmentType,
  getDocsForType,
  selectedVerdicts,
  matchesSelectedVerdicts,
  getDocServiceTags = () => [],
  highlightedDocumentIdentifiers = [],
}) {
  const { _t } = useTranslator();
  const compulsoryDocs =
    forensicDocsMap?.[employmentType]?.employment_status?.compulsory ?? [];
  const additionalDocs =
    forensicDocsMap?.[employmentType]?.employment_status?.additional ?? [];
  const allDocs = [...compulsoryDocs, ...additionalDocs];

  if (allDocs.length === 0) return null;

  const consumedDocKeys = new Set();
  const getDocKey = (doc) => getUniqueDocKey(doc);

  const employmentRows = allDocs.flatMap((item) => {
    const isCompulsory = compulsoryDocs.includes(item);
    const docs = dedupeDocs(
      getDocsForType(item, [DOCUMENT_CATEGORIES.EMPLOYMENT_STATUS])
    );
    const strictTypeDocs = docs.filter((doc) => doc?.type === item);
    // const fallbackCategoryDocs = docs.filter((doc) => doc?.type !== item);

    const availableStrict = strictTypeDocs.filter(
      (doc) => !consumedDocKeys.has(getDocKey(doc))
    );
    if (availableStrict.length > 0) {
      availableStrict.forEach((doc) => consumedDocKeys.add(getDocKey(doc)));
      return availableStrict.map((doc) => ({ item, doc }));
    }

    // If no strict type match, show missing only for compulsory docs
    if (isCompulsory) {
      return [{ item, doc: null }];
    }

    return [];
  });

  // Append remaining employment-category docs not used to satisfy required slots.
  const allEmploymentCategoryDocs = dedupeDocs(
    allDocs.flatMap((item) =>
      getDocsForType(item, [DOCUMENT_CATEGORIES.EMPLOYMENT_STATUS])
    )
  );
  const extraEmploymentDocs = allEmploymentCategoryDocs.filter(
    (doc) => !consumedDocKeys.has(getDocKey(doc))
  );
  const finalEmploymentRows = dedupeDocRows([
    ...employmentRows,
    ...extraEmploymentDocs.map((doc) => ({ item: doc?.type ?? "", doc })),
  ]);

  const filteredDocs =
    selectedVerdicts.length === 0
      ? finalEmploymentRows
      : finalEmploymentRows.filter(
          ({ doc }) => doc && matchesSelectedVerdicts(doc)
        );

  if (selectedVerdicts.length > 0 && filteredDocs.length === 0) return null;

  const totalDocs = filteredDocs.length;
  const uploadedDocs = filteredDocs.filter(({ doc }) => !!doc).length;

  let hasHighRisk = false;
  let hasWarning = false;
  let hasOthersDocument = false;
  let isScannedDoc = false;

  for (const { doc } of filteredDocs) {
    const fileName = doc?.file_name;
    const verdict = forensicDocumentReport?.[fileName]?.verdict;

    const isScanned =
      forensicDocumentReport?.[fileName]?.documentType ===
        "is_scanned_document" ||
      forensicDocumentReport?.[fileName]?.isScannedDocument === "yes";
    if (isScanned) {
      isScannedDoc = true;
      continue;
    }
    if (verdict === "HIGH_RISK") hasHighRisk = true;
    if (verdict === "WARNING") hasWarning = true;
  }

  const hasMissingDocs = uploadedDocs < totalDocs;

  for (const { doc } of filteredDocs) {
    if (isOtherUserDoc(doc)) {
      hasOthersDocument = true;
      break;
    }
  }

  const getPriority = (doc) => {
    if (!doc) return -1;
    const isScanned =
      forensicDocumentReport?.[doc?.file_name]?.documentType ===
        "is_scanned_document" ||
      forensicDocumentReport?.[doc?.file_name]?.isScannedDocument === "yes";
    if (isScanned) return verdictPriority["SCANNED"];
    const verdict = forensicDocumentReport?.[doc?.file_name]?.verdict;
    if (!verdict) return verdictPriority["WARNING"];
    return verdictPriority[verdict] ?? 3;
  };

  const sortedDocs = [...filteredDocs].sort((a, b) => {
    const docA = a?.doc;
    const docB = b?.doc;
    return getPriority(docA) - getPriority(docB);
  });

  return (
    <div className="documents-container-info-card">
      <div
        onClick={() => handleDropdownToggle("sec")}
        className="d-flex cursor-pointer justify-content-between documents-container-info-card-padding position-relative"
      >
        <div className="documents-container-info-card-img">
          <img src="/images/employment-status-icon.svg" alt="" />
          <span>{_t("employmentStatus")}</span>
        </div>
        <div className="d-flex align-items-center custom-gap-2">
          {!llShowOnly && hasOthersDocument && (
            <span className="other-doc-msg d-flex align-items-center custom-gap-2">
              <img src="/images/high-risk.svg" alt="" />
              {_t("othersDocs")}
            </span>
          )}
          {llShowOnly && isScannedDoc && (
            <div
              className="documents-container-info-card-icon"
              data-tooltip-id="scanned-doc-icon-2"
            >
              <img
                className="scanned-cross-icon"
                src="/images/search-with-cross-icon.svg"
                alt=""
              />
            </div>
          )}
          {/* verdict text */}
          {(hasHighRisk || hasWarning || hasMissingDocs) && (
            <div className="documents-container-info-card-icon">
              {hasMissingDocs && (
                <MissingDocsTag llShowOnly={llShowOnly} tooltipIndex={1} />
              )}
              {hasWarning && llShowOnly && (
                <img
                  src="/images/warning.svg"
                  alt=""
                  data-tooltip-id="warning-doc-icon-2"
                />
              )}
              {hasHighRisk && llShowOnly && (
                <img
                  src="/images/high-risk.svg"
                  alt=""
                  data-tooltip-id="high-risk-doc-icon-2"
                />
              )}
            </div>
          )}
          <ScannedTooltip index={2} />
          <MissingDocTooltip index={1} />
          <WarningTooltip index={2} />
          <HighRiskTooltip index={2} />
          <div className="doc-pl">
            <span>{_t("documents")}: </span>
            <span>
              {uploadedDocs}/{totalDocs}
            </span>
          </div>
          <div className="cursor-pointer arrow-icon-docs">
            {showDropdown.sec ? (
              <IoIosArrowUp size={18} style={{ color: "#253837" }} />
            ) : (
              <IoIosArrowDown size={18} style={{ color: "#253837" }} />
            )}
          </div>
        </div>
      </div>

      {showDropdown.sec && (
        <div className="doc-inner-container">
          {sortedDocs.map(({ item, doc }) => {
            const title = doc ? getDisplayDocName(doc) : _t(item, "", "fixed");

            return (
              <div
                key={doc?.file_name ?? `${item}-missing`}
                className={`d-flex doc-inner-container-padding justify-content-between position-relative align-items-center ${
                  documentMatchesIdentifiers(
                    doc,
                    highlightedDocumentIdentifiers
                  )
                    ? "tenant-document-navigation-highlight"
                    : ""
                }`}
                data-tenant-document-identifiers={JSON.stringify(
                  getTenantDocumentIdentifiers(doc)
                )}
                style={{
                  opacity: !doc ? 0.4 : 1,
                  pointerEvents: !doc ? "none" : "",
                  borderColor: llShowOnly
                    ? forensicDocumentReport?.[doc?.file_name]?.verdict ===
                      "HIGH_RISK"
                      ? "#ffe2e2"
                      : forensicDocumentReport?.[doc?.file_name]
                            ?.documentType === "is_scanned_document" ||
                          forensicDocumentReport?.[doc?.file_name]
                            ?.isScannedDocument === "yes"
                        ? "#fffbec"
                        : forensicDocumentReport?.[doc?.file_name]?.verdict ===
                            "WARNING"
                          ? "#fffbec"
                          : forensicDocumentReport?.[doc?.file_name]
                                ?.verdict === "NORMAL"
                            ? "transparent"
                            : forensicDocumentReport?.[doc?.file_name]
                                  ?.verdict === "TRUSTED"
                              ? "transparent"
                              : !doc
                                ? "rgb(197 193 193)"
                                : "transparent"
                    : isOtherUserDoc(doc)
                      ? "#FC9597"
                      : !doc
                        ? "#FACD7D"
                        : "transparent",
                }}
              >
                <div className="d-flex align-items-center doc-name-wrapper">
                  {llShowOnly && (
                    <>
                      <img
                        data-tooltip-id={`doc-second-dropdown-${doc?._id}`}
                        src={
                          forensicDocumentReport?.[doc?.file_name]
                            ?.documentType === "is_scanned_document" ||
                          forensicDocumentReport?.[doc?.file_name]
                            ?.isScannedDocument === "yes"
                            ? "/images/search-cross-with-bg.svg"
                            : forensicDocumentReport?.[doc?.file_name]
                                  ?.verdict === "HIGH_RISK"
                              ? "/images/high-risk-icon.svg"
                              : forensicDocumentReport?.[doc?.file_name]
                                    ?.verdict === "WARNING"
                                ? "/images/warning-icon.svg"
                                : forensicDocumentReport?.[doc?.file_name]
                                      ?.verdict === "NORMAL"
                                  ? "/images/normal-icon.svg"
                                  : forensicDocumentReport?.[doc?.file_name]
                                        ?.verdict === "TRUSTED"
                                    ? "/images/trusted-icon.svg"
                                    : doc
                                      ? "/images/warning-icon.svg"
                                      : "/images/missing-icon.svg"
                        }
                        alt=""
                      />
                      {haveValue(
                        forensicDocumentReport?.[doc?.file_name]
                          ?.explanationSummary?.["en"]
                      ) ||
                      haveValue(
                        forensicDocumentReport?.[doc?.file_name]
                          ?.recommendedAction?.["en"]
                      ) ||
                      haveValue(
                        forensicDocumentReport?.[doc?.file_name]?.score
                      ) ? (
                        <ReactTooltip
                          id={`doc-second-dropdown-${doc?._id}`}
                          noArrow
                          className="react-tooltip-styling details-skor-tooltip"
                          render={() => (
                            <div>
                              {forensicDocumentReport?.[doc?.file_name]
                                ?.explanationSummary?.[
                                currentLang === "en-us" ? "en" : "es"
                              ] && (
                                <>
                                  <p>
                                    <span>{_t("explanationSummary")}: </span>
                                    {forensicDocumentReport?.[doc?.file_name]
                                      ?.documentType ===
                                      "is_scanned_document" ||
                                    forensicDocumentReport?.[doc?.file_name]
                                      ?.isScannedDocument === "yes"
                                      ? _t(
                                          "thisDocumentIsAScanSoCouldNotRunForensicAnalysis"
                                        )
                                      : forensicDocumentReport?.[doc?.file_name]
                                          ?.explanationSummary?.[
                                          currentLang === "en-us" ? "en" : "es"
                                        ]}
                                  </p>
                                  <hr className="document-dropdown-tooltip-hr" />
                                </>
                              )}
                              {forensicDocumentReport?.[doc?.file_name]
                                ?.recommendedAction?.[
                                currentLang === "en-us" ? "en" : "es"
                              ] && (
                                <p>
                                  <span>{_t("recommendedAction")}: </span>
                                  {forensicDocumentReport?.[doc?.file_name]
                                    ?.documentType === "is_scanned_document" ||
                                  forensicDocumentReport?.[doc?.file_name]
                                    ?.isScannedDocument === "yes"
                                    ? _t(
                                        "toEnableForensicChecksAskTheTenantToUploadTheOriginalDigitalPdf"
                                      )
                                    : forensicDocumentReport?.[doc?.file_name]
                                        ?.recommendedAction?.[
                                        currentLang === "en-us" ? "en" : "es"
                                      ]}
                                </p>
                              )}
                              {!(
                                forensicDocumentReport?.[doc?.file_name]
                                  ?.documentType === "is_scanned_document" ||
                                forensicDocumentReport?.[doc?.file_name]
                                  ?.isScannedDocument === "yes"
                              ) &&
                                forensicDocumentReport?.[doc?.file_name]
                                  ?.score && (
                                  <p>
                                    <span>
                                      {_t("confidenceInAuthenticity")}:{" "}
                                    </span>
                                    {`${forensicDocumentReport?.[doc?.file_name]?.score}%`}
                                  </p>
                                )}
                            </div>
                          )}
                        />
                      ) : (
                        <ReactTooltip
                          id={`doc-second-dropdown-${doc?._id}`}
                          noArrow
                          className="react-tooltip-styling details-skor-tooltip"
                          render={() => (
                            <p>
                              <span>{_t("explanationSummary")}: </span>
                              {_t(
                                "notAbleToRunForensicCheckForThisDocument",
                                "Fall back text in document section tooltip when for a document forensic was not able to run.(Shown on hover)"
                              )}
                            </p>
                          )}
                        />
                      )}
                    </>
                  )}
                  <div>
                    <p className="docName">
                      {getDisplayDocName(doc)}
                      <DocServiceTags tags={getDocServiceTags(doc)} />
                      {doc?.type === "Working life certificate" &&
                        haveValue(
                          forensicAnalysis?.["Work Life Certificate"]
                            ?.dataInJSON?.reportDate
                        ) && (
                          <>
                            {" "}
                            (
                            {moment(
                              forensicAnalysis?.["Work Life Certificate"]
                                ?.dataInJSON?.reportDate
                            ).format("D MMMM YYYY")}
                            )
                          </>
                        )}
                      {!doc && `${_t(item, "", "fixed")}`}
                    </p>
                    {doc && (
                      <p className="doc-uploaded-date d-flex align-items-center">
                        <span className="doc-uploaded-date-text">
                          {_t("uploadedOn")}{" "}
                          {moment(doc?.created_at).format("DD-MM-YYYY")}
                        </span>
                        {isOtherUserDoc(doc) && (
                          <span className="others-doc-badge">
                            <img src="/images/user-icon.svg" alt="" />
                            <span title={`${getDocOwnerLabel(doc)}`}>
                              {_t(getOwnerBadgeTranslationKey(doc))}:{" "}
                              {getDocOwnerLabel(doc)}
                            </span>
                          </span>
                        )}
                      </p>
                    )}
                  </div>
                </div>
                <div className="d-flex align-items-center docs-view-btn-gap">
                  {isOtherUserDoc(doc) && !llShowOnly && !readOnly && (
                    <div
                      onClick={() => {
                        setDeleteDocumentConfirmation(true);
                        setselectedDocument(doc);
                      }}
                      className="delete cursor-pointer"
                    >
                      <img
                        src="/images/delete-icon-green.svg"
                        alt=""
                        className="deleteIcon"
                      />
                    </div>
                  )}
                  <div
                    onClick={(e) => handleViewDocument(e, doc)}
                    className={`d-flex align-items-center view-doc-button ${
                      doc ? "underline cursor-pointer" : ""
                    }`}
                  >
                    {doc ? (
                      <img
                        src="/images/view-eye-icon.svg"
                        alt=""
                        className="deleteIcon"
                      />
                    ) : (
                      _t("documentMissing")
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
